import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18n';
import { api } from '../services/api';

const FALLBACK_LOCALES = [
  { code: 'fa', name: 'فارسی', nativeName: 'فارسی', direction: 'rtl', intlLocale: 'fa-IR', currencyCode: 'IRT', currencyLabel: 'تومان', paymentProvider: 'sepehr', paymentEnabled: true, isDefault: true },
  { code: 'ckb', name: 'کوردی سورانی', nativeName: 'کوردی', direction: 'rtl', intlLocale: 'ckb-IQ', currencyCode: 'IQD', currencyLabel: 'دیناری عێراقی', paymentProvider: 'none', paymentEnabled: false },
  { code: 'en', name: 'انگلیسی', nativeName: 'English', direction: 'ltr', intlLocale: 'en-US', currencyCode: 'USD', currencyLabel: 'US Dollar', paymentProvider: 'none', paymentEnabled: false },
];

const LocaleContext = createContext(null);

const cleanPath = (path) => {
  const value = String(path || '/');
  return value.startsWith('/') ? value : `/${value}`;
};

export const LocaleProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [locales, setLocales] = useState(FALLBACK_LOCALES);
  const [localesLoading, setLocalesLoading] = useState(true);
  const [pageAlternates, setPageAlternates] = useState(null);

  const pathLocale = location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  const currentLocale = locales.find((item) => item.code === pathLocale)
    || locales.find((item) => item.isDefault)
    || locales[0];
  const isAdmin = location.pathname === '/admin-panel' || location.pathname.startsWith('/admin-panel/');
  const locale = isAdmin
    ? (locales.find((item) => item.code === 'fa') || currentLocale)
    : currentLocale;

  const reloadLocales = useCallback(() => {
    let active = true;
    setLocalesLoading(true);
    api.get('/api/locales')
      .then((response) => {
        if (active && Array.isArray(response.data) && response.data.length) {
          setLocales(response.data);
        }
      })
      .catch(() => {})
      .finally(() => active && setLocalesLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => reloadLocales(), [reloadLocales]);

  useEffect(() => {
    const reload = () => reloadLocales();
    window.addEventListener('haryad:locales-changed', reload);
    return () => window.removeEventListener('haryad:locales-changed', reload);
  }, [reloadLocales]);

  useEffect(() => {
    const language = i18n.hasResourceBundle(locale.code, 'translation') ? locale.code : 'fa';
    i18n.changeLanguage(language);
    document.documentElement.lang = locale.code;
    document.documentElement.dir = isAdmin ? 'rtl' : locale.direction;
    document.body.dir = isAdmin ? 'rtl' : locale.direction;
    document.body.dataset.locale = locale.code;
    localStorage.setItem('haryad-locale', locale.code);
  }, [isAdmin, locale.code, locale.direction]);

  useEffect(() => {
    setPageAlternates(null);
  }, [location.pathname]);

  const localePath = useCallback((path = '/') => {
    const normalized = cleanPath(path);
    if (normalized === '/admin-panel' || normalized.startsWith('/admin-panel/')) return normalized;
    const parts = normalized.split('/').filter(Boolean);
    if (locales.some((item) => item.code === parts[0])) return normalized;
    return normalized === '/' ? `/${locale.code}` : `/${locale.code}${normalized}`;
  }, [locale.code, locales]);

  const changeLocale = useCallback((nextCode) => {
    const nextLocale = locales.find((item) => item.code === nextCode && item.enabled !== false);
    if (!nextLocale) return;

    // From admin (always Persian UI), language switch opens the public site.
    if (isAdmin) {
      if (nextLocale.code === 'fa' && locale.code === 'fa') return;
      navigate(`/${nextLocale.code}`);
      return;
    }

    if (nextLocale.code === locale.code) return;

    if (pageAlternates) {
      if (pageAlternates[nextCode]) {
        navigate(pageAlternates[nextCode]);
        return;
      }
      const currentParts = location.pathname.split('/').filter(Boolean);
      const section = currentParts[1];
      if (section === 'products' || section === 'articles') {
        navigate(`/${nextLocale.code}/${section}`);
        return;
      }
    }

    const parts = location.pathname.split('/').filter(Boolean);
    if (locales.some((item) => item.code === parts[0])) parts.shift();
    const suffix = parts.length ? `/${parts.join('/')}` : '';
    navigate(`/${nextLocale.code}${suffix}${location.search}${location.hash}`);
  }, [isAdmin, locale.code, locales, location.hash, location.pathname, location.search, navigate, pageAlternates]);

  const safeIntlLocale = useCallback(() => {
    const candidate = locale.intlLocale || locale.code || 'fa-IR';
    try {
      new Intl.NumberFormat(candidate);
      return candidate;
    } catch (_) {
      return locale.code === 'en' ? 'en-US' : 'fa-IR';
    }
  }, [locale.code, locale.intlLocale]);

  const formatNumber = useCallback((value, options) => (
    new Intl.NumberFormat(safeIntlLocale(), options).format(Number(value) || 0)
  ), [safeIntlLocale]);

  const formatCurrency = useCallback((value, currencyLabel = locale.currencyLabel) => (
    `${formatNumber(value, { maximumFractionDigits: 2 })} ${currencyLabel || locale.currencyCode || ''}`.trim()
  ), [formatNumber, locale.currencyCode, locale.currencyLabel]);

  const formatDate = useCallback((value, options = {}) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(safeIntlLocale(), options).format(date);
    } catch (_) {
      return new Intl.DateTimeFormat('fa-IR', options).format(date);
    }
  }, [safeIntlLocale]);

  const value = useMemo(() => ({
    locale,
    locales,
    localesLoading,
    localePath,
    changeLocale,
    formatNumber,
    formatCurrency,
    formatDate,
    setPageAlternates,
    pageAlternates,
    isAdmin,
  }), [changeLocale, formatCurrency, formatDate, formatNumber, isAdmin, locale, localePath, locales, localesLoading, pageAlternates]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside LocaleProvider');
  return value;
};
